import { useCallback, useMemo, useState } from 'react';

interface IUseCarouselOffsetParams {
  initialOffset?: number;
  itemWidth: number;
  itemCount: number;
  containerWidth: number;
  allowFolding?: boolean;
}

interface IUseCarouselOffsetResult {
  offset: number;
  maxOffset: number;
  increaseOffset: VoidFunction;
  decreaseOffset: VoidFunction;
  scrollToEnd: VoidFunction;
  resetOffset: VoidFunction;
}

/**
 * Reusable hook that helps to control the scrolling state of the Carousel component
 * @param {Object} config Configuration of the hook
 * @param {number} config.initialOffset Initial scrolling offset from the beginning of the carousel pane
 * @param {number} config.itemWidth Width of the single item inside the carousel
 * @param {number} config.itemCount Number of items inside the carousel
 * @param {number} config.containerWidth The width of the carousel
 *
 * @returns {IUseCarouselOffsetResult} Offset data and handlers for changing
 *
 * @example
 * // init
 * const { offset, decreaseOffset, increaseOffset } = useCarouselOffset({ itemWidth: 232, itemCount: 5, containerWidth: 768 });
 *
 * // use offset value
 * console.log(offset);
 *
 * // control offset value
 * <button onClick={decreaseOffset}>Scroll left</button>
 * <button onClick={increaseOffset}>Scroll right</button>
 */
const useCarouselOffset = ({
  initialOffset,
  itemWidth,
  itemCount,
  containerWidth,
  allowFolding = true,
}: IUseCarouselOffsetParams): IUseCarouselOffsetResult => {
  const [offset, setOffset] = useState(() => initialOffset ?? 0);

  const maxOffset = useMemo(() => itemWidth * itemCount - containerWidth, [itemWidth, itemCount, containerWidth]);

  const decreaseOffset = useCallback(() => {
    if (!allowFolding && offset === 0) {
      return;
    }
    setOffset((oldOffset) => {
      let newOffset = itemWidth * Math.floor((oldOffset - 1) / itemWidth);
      if (newOffset < 0) {
        newOffset = maxOffset;
      }
      return newOffset;
    });
  }, [itemWidth, maxOffset, offset, allowFolding]);

  const increaseOffset = useCallback(() => {
    if (!allowFolding && offset === maxOffset) {
      return;
    }
    setOffset((oldOffset) => {
      if (oldOffset === maxOffset) {
        return 0;
      }
      return Math.min(itemWidth * Math.ceil((oldOffset + 1) / itemWidth), maxOffset);
    });
  }, [itemWidth, maxOffset, offset, allowFolding]);

  const scrollToEnd = useCallback(() => {
    if (offset < maxOffset) {
      setOffset(maxOffset);
    }
  }, [offset, maxOffset]);

  const resetOffset = useCallback(
    (toInitialOffset = false) => {
      setOffset(toInitialOffset && initialOffset ? initialOffset : 0);
    },
    [initialOffset],
  );

  return {
    offset,
    maxOffset,
    decreaseOffset,
    scrollToEnd,
    increaseOffset,
    resetOffset,
  };
};

export default useCarouselOffset;
